import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import { getData, postData, patchData, deleteData } from "../api/ApiServices";
import "../styles/AddItemModal.new.css";
import { Contributor, ContributorResponse } from "../models/contributortypes";
import AddContributorForm from "../forms/AddContributorForm";
import Pagination from "../view/common/pagination/Pagination";
import TableComponent from "./TableComponent";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import CustomAlert from "../view/common/alert/CustomAlert";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";

const NewContributor: React.FC = () => {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [cityOptions, setCityOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [gstStateCodes, setGstStateCodes] = useState<
    { label: string; value: string }[]
  >([]);
  const [editingContributor, setEditingContributor] =
    useState<Contributor | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [cityResponse, setCityResponse] = useState<any>();

  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const  permission = useSelector((state:RootState) => state.permissions)
    
  const [initializeContributor, iscontributorInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    iscontributorInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeContributor) {
      fetchContributors();
    }
  }, [
    initializeContributor,
    searchTerm,
    currentPage,
    itemsPerPage,
    filters,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchCities();
    fetchCurrencies();
    fetchGstStateCodes();
  }, []);

  useEffect(() => {
    console.log(cityResponse);
  }, [cityResponse]);

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const fetchContributors = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<{ data: ContributorResponse[] }>(
        "/contributors",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: filters,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      );
      if (response?.data?.data) {
        setContributors(response.data.data);
        setTotalItems(response.data.pagination.total || 0);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setContributors([]);
      }
    } catch (error) {
      console.error("Error fetching contributors:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const fetchCities = async () => {
    try {
      const response: any = await getData<{
        data: { cityName: string; _id: string }[];
      }>("/citymasters", { page: 1, limit: 100 });
      setCityOptions(
        response.data.data.map((c: { cityName: string; _id: string }) => ({
          label: c.cityName,
          value: c._id,
        }))
      );
      setCityResponse(response.data.data);
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCityOptions([]);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response: any = await getData<{
        data: { name: string; _id: string }[];
      }>("/currencymaster", { page: 1, limit: 100 });
      setCurrencyOptions(
        response.data.data.map((c: { name: string; _id: string }) => ({
          label: c.name,
          value: c._id || "",
        }))
      );
    } catch (error) {
      console.error("Error fetching currencies:", error);
      setCurrencyOptions([]);
    }
  };

  const fetchGstStateCodes = async () => {
    try {
      const response: any = await getData("/gststatecode");
      setGstStateCodes(
        response.data.map((item: any) => ({
          label: `${item.gstCode}`,
          value: item.gstCode.toString(),
        }))
      );
      console.log(gstStateCodes);
    } catch (error) {
      console.error("Error fetching GST State Codes:", error);
    }
  };

  const handleAddOrEdit = async (contributor: Contributor) => {
    console.log("edited contributor: ", contributor);
    setIsLoading(true);
    try {
      if (editingContributor) {
        const hasChanges = Object.keys(contributor).some((key) => {
          if (key === "_id") return false;

          const originalValue = (editingContributor as any)[key];
          const newValue = (contributor as any)[key];

          if (
            typeof originalValue === "object" &&
            originalValue !== null &&
            typeof newValue === "object" &&
            newValue !== null
          ) {
            return JSON.stringify(originalValue) !== JSON.stringify(newValue);
          }

          return originalValue !== newValue;
        });

        if (!hasChanges) {
          configureAlert(
            `No changes were made to the contributor.`,
            () => {
              closeAlert();
              setIsAdding(false);
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
          setIsLoading(false);
          return;
        }

        await patchData(
          "/contributors",
          editingContributor._id || "",
          contributor
        );
        configureAlert(
          `Contributor has been updated successfully.`,
          () => {
            fetchContributors();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
        setIsAdding(false);
      } else {
        const response: any = await postData("/contributors", contributor);
        console.log(response);

        if (response?.statusCode === 1126) {
          configureAlert(
            `Contributor has been added successfully.`,
            () => {
              fetchContributors();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000 // Auto-close after 3 seconds
          );
        } else {
          throw new Error("Unexpected response!");
        }
        setIsAdding(false);
      }
    } catch (error: any) {
      console.error("Error saving contributor:", error);

      // Handle 400 response as duplicate entry
      configureAlert(
        `Duplicate entry!`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000 // Auto-close after 3 seconds
      );
      setIsAdding(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContributor = async (id: string) => {
    const contributorToDelete = contributors.find((c) => c._id === id);

    if (contributorToDelete) {
      // Show confirmation popup for deletion - no auto-close
      configureAlert(
        `Are you sure you want to InActive this contributor?`,
        async () => {
          closeAlert(); // Close the confirmation dialog first
          setIsLoading(true);

          try {
            await deleteData("/contributors", id);
            // After successful deletion, show success message with auto-close
            configureAlert(
              `Contributor has been InActive successfully.`,
              () => {
                fetchContributors();
                closeAlert(); // Explicitly close the alert when OK is clicked
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 // Auto-close after 3 seconds
            );
          } catch (error) {
            console.error("Error dInactive contributor:", error);
            configureAlert(
              "Failed to Inactive contributor.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 // Auto-close after 3 seconds
            );
          } finally {
            setIsLoading(false);
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null // No auto-close for confirmation
      );
    }
  };

  const handleAddClick = () => {
    setEditingContributor(null);
    setIsAdding(true);
  };

  const handleEditClick = (contributor: Contributor) => {
    setEditingContributor(contributor);
    setIsAdding(true);
  };

  const handleClosePopup = () => {
    if (isAdding) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsAdding(false);
          setEditingContributor(null);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null // No auto-close for confirmation
      );
    } else {
      setIsAdding(false);
      setEditingContributor(null);
    }
  };

  const columns = [
    { key: "type", label: "Type" },
    { key: "taxType", label: "Tax Type" },
    { key: "gstNo", label: "GST No" },
    { key: "code", label: "Code" },
    { key: "name", label: "Name" },
    { key: "status", label: "Status" },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Contributors</span>
            </div>
            {permission.menus.contributors.create ?
            <div
              className="items-button-container tooltip-wrapper"
              onClick={handleAddClick}
            >
              <button className="item-models-add-btn">
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>
      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={contributors}
            onEdit={handleEditClick}
            onDelete={handleDeleteContributor}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
          />
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
      {isAdding && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h2>
                {editingContributor
                  ? "Edit Contributor"
                  : "Add New Contributor"}
              </h2>
              <button className="close-button" onClick={handleClosePopup}>
                <X size={20} />
              </button>
            </div>
            <div className="popup-content">
              <AddContributorForm
                onSave={handleAddOrEdit}
                onCancel={handleClosePopup}
                cities={cityOptions}
                cityResponse={cityResponse}
                currencies={currencyOptions}
                gstStateCodes={gstStateCodes}
                isProcessing={isLoading}
                isEditing={!!editingContributor}
                editingContributor={editingContributor || undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* CustomAlert Component */}
      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default NewContributor;
